/**
 * LegalLayout — shared shell for /privacy and /terms.
 *
 * Same nav + footer as the marketing landing so users never feel dropped
 * into a bare "wall of text" page. Legal copy sits inside a narrow reading
 * column (max-w-3xl) for comfortable line-lengths, uses the same Manrope +
 * Outfit type stack as the rest of the site, and preserves the app's warm
 * cream background.
 */
import { Nav, Footer } from "@/components/landing";
import { useAuth } from "@/lib/auth";
import { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";

export default function LegalLayout({ title, updated, children, testid }) {
  const { user } = useAuth();

  // Scroll to top on route mount — otherwise clicking Privacy from a
  // deep-scrolled landing lands users mid-legal-page.
  useEffect(() => { window.scrollTo(0, 0); }, []);

  return (
    <div className="grain min-h-screen" style={{ background: "var(--rammy-bg)", fontFamily: "'Manrope', system-ui, sans-serif" }}>
      <Nav authed={!!user} />
      <main className="pt-28 pb-20" data-testid={testid}>
        <div className="max-w-3xl mx-auto px-6 md:px-8">
          <Link
            to="/"
            className="inline-flex items-center gap-1.5 text-[13px] text-[var(--rammy-text-sec)] hover:text-[var(--rammy-text)] transition"
            data-testid="legal-back-home"
          >
            <ArrowLeft size={14} strokeWidth={1.8} /> Back to Rammy
          </Link>
          <h1 className="mt-6 font-display text-4xl sm:text-5xl leading-[1.05] tracking-tight text-[var(--rammy-text)]">
            {title}
          </h1>
          {updated && (
            <div className="mt-3 text-[13px] text-[var(--rammy-text-muted)]" data-testid="legal-updated">
              Last updated · {updated}
            </div>
          )}
          <div
            className="prose prose-slate max-w-none mt-10 text-[15.5px] leading-[1.75] text-[var(--rammy-text)]
              [&_h2]:font-display [&_h2]:text-2xl [&_h2]:mt-12 [&_h2]:mb-4 [&_h2]:tracking-tight
              [&_h3]:font-display [&_h3]:text-lg [&_h3]:mt-8 [&_h3]:mb-3 [&_h3]:font-medium
              [&_p]:my-4 [&_p]:text-[var(--rammy-text-sec)]
              [&_ul]:my-4 [&_ul]:pl-5 [&_ul]:list-disc [&_li]:my-1.5 [&_li]:text-[var(--rammy-text-sec)]
              [&_a]:text-[var(--rammy-cyan-dark)] [&_a]:underline [&_a]:underline-offset-2 [&_a:hover]:opacity-80
              [&_strong]:text-[var(--rammy-text)] [&_strong]:font-semibold"
          >
            {children}
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
}
